import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';
import { Grid } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { SINGLE_NAVIGATION_PATHS } from '../../constants/single-navigation-paths';
import {
    CollapseContainer,
    FilterContainer,
    GraphContainer,
} from './components';
import { PageWrapper } from 'components';
import { draggablePanelStyles, getNormalizedDictionaryColors } from 'lib';
import { useDictionaryList } from 'hooks';
import {
    gridSize,
    isUnsupportedLGQuery,
    styles,
} from './components/lifegraph-explorer/lifegraph-explorer-helper';
import { LifeGraphExplorerPageFilter } from './components/lifegraph-explorer/lifegraph-explorer.types';
import {
    advancedFilterDefaults,
    AdvancedFormInputs,
    FilterMode,
} from './components/lifegraph-filter/life-graph-filters-helpers';

export const LifeGraphExplorerPage = (): React.JSX.Element => {
    const location = useLocation();
    const navigate = useNavigate();
    const [filter, setFilter] = useState<LifeGraphExplorerPageFilter>();
    const [filterMode, setFilterMode] = useState<FilterMode>('Basic');
    const [defaultQuery, setDefaultQuery] = useState<AdvancedFormInputs>(
        advancedFilterDefaults
    );
    const [forceUpdate, setForceUpdate] = useState<boolean>(false);
    const { data } = useDictionaryList();
    const [displayMode, setDisplayMode] = useState<string>('3D');
    const [collapse, setCollapse] = useState<boolean>(true);
    const containerRef = useRef<HTMLInputElement>(null);
    const filterRef = useRef<HTMLInputElement>(null);
    const [canvasWidth, setCanvasWidth] = useState<number>(0);
    const [canvasHeight, setCanvasHeight] = useState<number>(0);
    const [unsupportedQuery, setUnsupportedQuery] = useState<boolean>(false);
    const [showGraphBtn, setShowGraphBtn] = useState<boolean>(false);
    const [isCanvasHeightSmall, setIsCanvasHeightSmall] =
        useState<boolean>(false);

    const handleFilterChange = (
        newFilter: LifeGraphExplorerPageFilter
    ): void => {
        if (newFilter === filter) {
            setForceUpdate(true);
        } else {
            setFilter(newFilter);
        }
    };
    const handleSetFilter = (newFilter: LifeGraphExplorerPageFilter): void => {
        if (
            newFilter &&
            typeof newFilter === 'string' &&
            filterMode === 'Advanced'
        ) {
            if (isUnsupportedLGQuery(newFilter)) {
                setUnsupportedQuery(true);
                setFilter(newFilter);
            } else {
                setUnsupportedQuery(false);
                handleFilterChange(newFilter);
            }
        } else {
            handleFilterChange(newFilter);
        }
    };

    useEffect(() => {
        if (location?.state) {
            const tqlQuery = location.state;
            setFilterMode('Advanced');
            setDefaultQuery({ query: tqlQuery });
            handleSetFilter(tqlQuery);
        }
    }, [location.state]);

    useLayoutEffect(() => {
        const updateDimensions = (): void => {
            if (containerRef.current) {
                let height: number =
                    containerRef.current.clientHeight -
                    (filterRef?.current?.clientHeight ?? 0);

                if (filterMode === 'Advanced') {
                    setCanvasHeight(height - 57);
                    if (height < 345) {
                        setIsCanvasHeightSmall(true);
                    } else {
                        setIsCanvasHeightSmall(false);
                    }
                } else {
                    setCanvasHeight(height);
                }

                setCanvasWidth(containerRef.current.clientWidth);
            }
        };

        const observer = new ResizeObserver(() => {
            updateDimensions();
        });

        if (filterRef.current) {
            observer.observe(filterRef.current);
        }

        globalThis.addEventListener('resize', updateDimensions);

        return () => {
            globalThis.removeEventListener('resize', updateDimensions);
        };
    }, [filterMode]);

    const colors: Record<string, string> = getNormalizedDictionaryColors(
        data?.data ?? []
    );

    const onDisplayModeChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ): void => {
        setDisplayMode((event.target as HTMLInputElement).value);
    };
    const toggleFilterView = (): void => {
        globalThis.dispatchEvent(new Event('resize'));
        setCollapse((preVal: boolean): boolean => {
            return !preVal;
        });
    };

    const handleResize = (): void => {
        globalThis.dispatchEvent(new Event('resize'));
    };

    const handleUnsupportedQueryClick = (): void => {
        navigate(SINGLE_NAVIGATION_PATHS.TQL_EDITOR, { state: filter });
    };

    return (
        <PageWrapper
            title='LifeGraph Explorer'
            childrenContainerStyles={styles.childrenContainerHeight}
        >
            {filterMode === 'Basic' ? (
                <Grid sx={styles.box} ref={containerRef}>
                    <Grid item xs={gridSize}>
                        <GraphContainer
                            filter={filter}
                            canvasHeight={canvasHeight}
                            canvasWidth={canvasWidth}
                            displayMode={displayMode}
                            showGraphBtn={showGraphBtn}
                            onDisplayModeChange={onDisplayModeChange}
                            data={data?.data ?? []}
                            colors={colors}
                            setShowGraphBtn={setShowGraphBtn}
                            forceUpdate={forceUpdate}
                            setForceUpdate={setForceUpdate}
                        />
                    </Grid>
                    <Grid item xs={gridSize} ref={filterRef}>
                        <FilterContainer
                            collapse={collapse}
                            toggleFilterView={toggleFilterView}
                        />
                        <CollapseContainer
                            collapse={collapse}
                            filter={filter}
                            handleSetFilter={handleSetFilter}
                            data={data?.data ?? []}
                            filterMode={filterMode}
                            setFilterMode={setFilterMode}
                            defaultQuery={defaultQuery}
                            setDefaultQuery={setDefaultQuery}
                        />
                    </Grid>
                </Grid>
            ) : (
                <Grid sx={styles.box} ref={containerRef}>
                    <PanelGroup
                        direction='vertical'
                        onResizeCapture={handleResize}
                    >
                        <Panel>
                            <Grid item xs={gridSize}>
                                <GraphContainer
                                    filter={filter}
                                    canvasHeight={canvasHeight}
                                    canvasWidth={canvasWidth}
                                    displayMode={displayMode}
                                    showGraphBtn={showGraphBtn}
                                    onDisplayModeChange={onDisplayModeChange}
                                    data={data?.data ?? []}
                                    colors={colors}
                                    setShowGraphBtn={setShowGraphBtn}
                                    forceUpdate={forceUpdate}
                                    setForceUpdate={setForceUpdate}
                                    isCanvasHeightSmall={isCanvasHeightSmall}
                                    unsupportedQuery={unsupportedQuery}
                                    handleUnsupportedQueryClick={
                                        handleUnsupportedQueryClick
                                    }
                                />
                                <FilterContainer
                                    collapse={collapse}
                                    toggleFilterView={toggleFilterView}
                                />
                            </Grid>
                        </Panel>
                        <Panel
                            defaultSize={34}
                            maxSize={60}
                            minSize={32}
                            style={{
                                display: collapse ? 'initial' : 'none',
                            }}
                        >
                            <Grid
                                item
                                xs={gridSize}
                                ref={filterRef}
                                sx={draggablePanelStyles.panelContainerStyles}
                            >
                                {collapse && (
                                    <PanelResizeHandle
                                        style={
                                            draggablePanelStyles.panelResizeHandleStyles
                                        }
                                    />
                                )}
                                <CollapseContainer
                                    collapse={collapse}
                                    filter={filter}
                                    handleSetFilter={handleSetFilter}
                                    data={data?.data ?? []}
                                    filterMode={filterMode}
                                    setFilterMode={setFilterMode}
                                    defaultQuery={defaultQuery}
                                    setDefaultQuery={setDefaultQuery}
                                    collapseContainerStyles={
                                        styles.collapseContainerStyles
                                    }
                                    collapseInnerContainerStyles={
                                        styles.collapseInnerContainerStyles
                                    }
                                />
                            </Grid>
                        </Panel>
                    </PanelGroup>
                </Grid>
            )}
        </PageWrapper>
    );
};
